/**
 * Unified funnel flow calculator for consistent width/flow calculations
 * across both funnel and Sankey visualizations
 */

export interface FlowStep {
  id: string;
  name: string;
  count: number;
  parentCount: number;
  conversionRate: number;
  flowWidth: number; // 0-100 percentage for visualization
  color: string;
  level: number; // depth in funnel
  branch?: string; // for split flows (high/low rating)
}

export interface FlowLink {
  source: string;
  target: string;
  value: number;
  color: string;
  conversionRate: number;
}

export interface ProcessedFunnelData {
  steps: FlowStep[];
  links: FlowLink[];
  branches: {
    high: FlowStep[];
    low: FlowStep[];
    main: FlowStep[];
    rewards: FlowStep[];
  };
  metrics: {
    totalSessions: number;
    overallConversion: number;
    highRatingRate: number;
    rewardRedemptionRate: number;
  };
}

export interface RawFunnelData {
  step: string;
  count: number;
  conversionRate: number;
  dropOff: number;
}

export interface StarRatingData {
  rating: number;
  count: number;
  percentage: number;
}

export interface TagScanData {
  tagId: string;
  tagName: string;
  count: number;
  percentage: number;
}

export interface RewardTrackingData {
  step: string;
  count: number;
  rewardType: string;
  totalValue: number;
  avgValue: number;
  percentage: number;
}

// Color scheme for consistent theming
export const FLOW_COLORS = {
  qrScans: '#6366F1',      // Indigo
  main: '#3B82F6',         // Blue
  rating: '#EAB308',       // Yellow
  high: '#10B981',         // Green
  low: '#F59E0B',          // Orange
  rewards: '#8B5CF6',      // Purple
  validation: '#06B6D4',   // Cyan
  error: '#EF4444',        // Red
  neutral: '#6B7280'       // Gray
};

/**
 * Calculate flow width ensuring no step is wider than its parent
 * Uses absolute count proportion to maintain true funnel shape
 */
export function calculateFlowWidth(
  current: number, 
  maxValue: number, 
  maxWidth: number = 100,
  minWidth: number = 5
): number {
  // Input validation
  if (typeof current !== 'number' || current < 0) current = 0;
  if (typeof maxValue !== 'number' || maxValue <= 0) maxValue = 1;
  if (typeof maxWidth !== 'number' || maxWidth <= 0) maxWidth = 100;
  if (typeof minWidth !== 'number' || minWidth < 0) minWidth = 5;
  
  // If no current value, return minimum width
  if (current === 0) return minWidth;
  
  // Calculate truly proportional width: current/max * maxWidth
  // This ensures 10 is exactly 2x bigger than 5
  const proportionalWidth = (current / maxValue) * maxWidth;
  
  // Apply minimum width for visibility
  return Math.max(minWidth, proportionalWidth);
}

/**
 * Process raw funnel data into structured flow data
 */
export function processFunnelData(
  rawFunnelData: RawFunnelData[],
  starRatings: StarRatingData[],
  tagScans: TagScanData[],
  rewardTracking: RewardTrackingData[],
  starThreshold: number = 4
): ProcessedFunnelData {
  // Input validation
  if (!Array.isArray(rawFunnelData)) rawFunnelData = [];
  if (!Array.isArray(starRatings)) starRatings = [];
  if (!Array.isArray(tagScans)) tagScans = [];
  if (!Array.isArray(rewardTracking)) rewardTracking = [];
  if (typeof starThreshold !== 'number' || starThreshold < 1 || starThreshold > 5) {
    starThreshold = 4;
  }

  const steps: FlowStep[] = [];
  const links: FlowLink[] = [];
  
  // Helper to find step data with null checks
  const findStepData = (stepName: string) => {
    const found = rawFunnelData.find(step => step && step.step === stepName);
    return found || { step: stepName, count: 0, conversionRate: 0, dropOff: 0 };
  };
  
  // Helper to find reward data with null checks
  const findRewardData = (stepName: string) => {
    const found = rewardTracking.find(reward => reward && reward.step === stepName);
    return found || { step: stepName, count: 0, rewardType: '', totalValue: 0, avgValue: 0, percentage: 0 };
  };
  
  // Calculate totals with null safety
  const totalTagScans = Math.max(0, tagScans.reduce((sum, tag) => sum + (tag?.count || 0), 0));
  const totalRatings = Math.max(0, starRatings.reduce((sum, rating) => sum + (rating?.count || 0), 0));
  const aboveThresholdCount = Math.max(0, starRatings
    .filter(r => r && typeof r.rating === 'number' && r.rating >= starThreshold)
    .reduce((sum, r) => sum + (r?.count || 0), 0));
  const belowThresholdCount = Math.max(0, starRatings
    .filter(r => r && typeof r.rating === 'number' && r.rating < starThreshold)
    .reduce((sum, r) => sum + (r?.count || 0), 0));
  
  // Get step data
  const pageLoadedData = findStepData('page_loaded');
  const ratingSelectedData = findStepData('rating_selected');
  const emailStepData = findStepData('email_step_shown');
  const emailEnteredData = findStepData('email_entered');
  const emailSkippedData = findStepData('email_skipped');
  const redirectedData = findStepData('redirected_to_google');
  const reviewValidatedData = findStepData('review_validated');
  const feedbackFormData = findStepData('feedback_form_shown');
  const reviewSubmittedData = findStepData('review_submitted');
  
  // Main funnel flow
  const mainSteps = [
    {
      id: 'qr_scans',
      name: 'QR Code Scans',
      count: totalTagScans,
      parentCount: totalTagScans,
      level: 0,
      color: FLOW_COLORS.qrScans
    },
    {
      id: 'page_loaded',
      name: 'Page Loaded',
      count: pageLoadedData.count,
      parentCount: totalTagScans,
      level: 1,
      color: FLOW_COLORS.main
    },
    {
      id: 'rating_selected',
      name: 'Rating Selected',
      count: ratingSelectedData.count,
      parentCount: pageLoadedData.count,
      level: 2,
      color: FLOW_COLORS.rating
    }
  ];
  
  // Calculate email interactions for high ratings - use proper proportions
  const ratingProportion = ratingSelectedData.count > 0 ? (aboveThresholdCount / ratingSelectedData.count) : 0;
  const highEmailEntered = Math.round(emailEnteredData.count * ratingProportion);
  const highEmailSkipped = Math.round(emailSkippedData.count * ratingProportion);
  const highEmailTotal = highEmailEntered + highEmailSkipped;

  // High rating flow
  const highRatingSteps = [
    {
      id: 'high_ratings',
      name: 'High Ratings',
      count: aboveThresholdCount,
      parentCount: ratingSelectedData.count,
      level: 3,
      branch: 'high',
      color: FLOW_COLORS.high
    },
    {
      id: 'high_email_step',
      name: 'Email Step',
      count: Math.round(highEmailTotal),
      parentCount: aboveThresholdCount,
      level: 4,
      branch: 'high',
      color: FLOW_COLORS.high
    },
    {
      id: 'high_email_entered',
      name: 'Email Entered',
      count: Math.round(highEmailEntered),
      parentCount: Math.round(highEmailTotal),
      level: 5,
      branch: 'high',
      color: FLOW_COLORS.high
    },
    {
      id: 'high_email_skipped',
      name: 'Email Skipped',
      count: Math.round(highEmailSkipped),
      parentCount: Math.round(highEmailTotal),
      level: 5,
      branch: 'high',
      color: FLOW_COLORS.high
    },
    {
      id: 'redirected_to_google',
      name: 'Redirected to Google',
      count: redirectedData.count,
      parentCount: Math.round(highEmailTotal),
      level: 6,
      branch: 'high',
      color: FLOW_COLORS.high
    },
    {
      id: 'review_validated',
      name: 'Review Validated',
      count: reviewValidatedData.count,
      parentCount: redirectedData.count,
      level: 7,
      branch: 'high',
      color: FLOW_COLORS.validation
    }
  ];
  
  // Calculate email interactions for low ratings - use proper proportions
  const lowRatingProportion = ratingSelectedData.count > 0 ? (belowThresholdCount / ratingSelectedData.count) : 0;
  const lowEmailEntered = Math.round(emailEnteredData.count * lowRatingProportion);
  const lowEmailSkipped = Math.round(emailSkippedData.count * lowRatingProportion);
  const lowEmailTotal = lowEmailEntered + lowEmailSkipped;

  // Low rating flow
  const lowRatingSteps = [
    {
      id: 'low_ratings',
      name: 'Low Ratings',
      count: belowThresholdCount,
      parentCount: ratingSelectedData.count,
      level: 3,
      branch: 'low',
      color: FLOW_COLORS.low
    },
    {
      id: 'low_email_step',
      name: 'Email Step',
      count: Math.round(lowEmailTotal),
      parentCount: belowThresholdCount,
      level: 4,
      branch: 'low',
      color: FLOW_COLORS.low
    },
    {
      id: 'low_email_entered',
      name: 'Email Entered',
      count: Math.round(lowEmailEntered),
      parentCount: Math.round(lowEmailTotal),
      level: 5,
      branch: 'low',
      color: FLOW_COLORS.low
    },
    {
      id: 'low_email_skipped',
      name: 'Email Skipped',
      count: Math.round(lowEmailSkipped),
      parentCount: Math.round(lowEmailTotal),
      level: 5,
      branch: 'low',
      color: FLOW_COLORS.low
    },
    {
      id: 'feedback_form_shown',
      name: 'Feedback Form',
      count: feedbackFormData.count,
      parentCount: Math.round(lowEmailTotal),
      level: 6,
      branch: 'low',
      color: FLOW_COLORS.low
    },
    {
      id: 'review_submitted',
      name: 'Review Submitted',
      count: reviewSubmittedData.count,
      parentCount: feedbackFormData.count,
      level: 7,
      branch: 'low',
      color: FLOW_COLORS.low
    }
  ];
  
  // Reward tracking flow
  const rewardSteps = [
    'reward_offered',
    'reward_accepted', 
    'reward_declined',
    'reward_email_sent',
    'pass_download_started',
    'pass_downloaded',
    'pass_installed',
    'reward_redeemed'
  ];
  
  const rewardTrackingSteps = rewardSteps.map((step, index) => {
    const data = findRewardData(step);
    const previousStep = index > 0 ? findRewardData(rewardSteps[index - 1]) : null;
    
    return {
      id: step,
      name: step.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      count: data.count,
      parentCount: previousStep ? previousStep.count : aboveThresholdCount,
      level: 6 + index,
      branch: 'rewards',
      color: FLOW_COLORS.rewards
    };
  }).filter(step => step.count > 0);
  
  // Combine all steps
  const allSteps = [...mainSteps, ...highRatingSteps, ...lowRatingSteps, ...rewardTrackingSteps];
  
  // Calculate flow widths and conversion rates with true proportional scaling
  const processedSteps: FlowStep[] = [];
  
  // Find the maximum count across all steps for proportional scaling
  const maxCount = Math.max(...allSteps.map(step => step.count), 1);
  
  // Sort steps by level to process in order
  const sortedSteps = allSteps.sort((a, b) => a.level - b.level);
  
  sortedSteps.forEach(step => {
    const conversionRate = Math.min(100, Math.max(0, step.parentCount > 0 ? (step.count / step.parentCount) * 100 : 0));
    
    // Calculate truly proportional flow width based on the maximum value
    // This ensures that 10 is exactly 2x bigger than 5, etc.
    const flowWidth = calculateFlowWidth(step.count, maxCount, 100, 5);
    
    processedSteps.push({
      ...step,
      conversionRate,
      flowWidth
    });
  });
  
  // Generate links
  const generateLinks = (steps: FlowStep[]) => {
    const links: FlowLink[] = [];
    
    // Main flow links
    links.push({
      source: 'qr_scans',
      target: 'page_loaded',
      value: pageLoadedData.count,
      color: FLOW_COLORS.main,
      conversionRate: Math.min(100, Math.max(0, totalTagScans > 0 ? (pageLoadedData.count / totalTagScans) * 100 : 0))
    });
    
    links.push({
      source: 'page_loaded',
      target: 'rating_selected',
      value: ratingSelectedData.count,
      color: FLOW_COLORS.rating,
      conversionRate: Math.min(100, Math.max(0, pageLoadedData.count > 0 ? (ratingSelectedData.count / pageLoadedData.count) * 100 : 0))
    });
    
    // Rating split links
    if (aboveThresholdCount > 0) {
      links.push({
        source: 'rating_selected',
        target: 'high_ratings',
        value: aboveThresholdCount,
        color: FLOW_COLORS.high,
        conversionRate: Math.min(100, Math.max(0, totalRatings > 0 ? (aboveThresholdCount / totalRatings) * 100 : 0))
      });
    }
    
    if (belowThresholdCount > 0) {
      links.push({
        source: 'rating_selected',
        target: 'low_ratings',
        value: belowThresholdCount,
        color: FLOW_COLORS.low,
        conversionRate: Math.min(100, Math.max(0, totalRatings > 0 ? (belowThresholdCount / totalRatings) * 100 : 0))
      });
    }
    
    // High rating flow links
    if (aboveThresholdCount > 0) {
      links.push({
        source: 'high_ratings',
        target: 'high_email',
        value: aboveThresholdCount,
        color: FLOW_COLORS.high,
        conversionRate: 100
      });
      
      if (redirectedData.count > 0) {
        links.push({
          source: 'high_email',
          target: 'redirected_to_google',
          value: redirectedData.count,
          color: FLOW_COLORS.high,
          conversionRate: Math.min(100, Math.max(0, aboveThresholdCount > 0 ? (redirectedData.count / aboveThresholdCount) * 100 : 0))
        });
      }
    }
    
    // Low rating flow links
    if (belowThresholdCount > 0) {
      links.push({
        source: 'low_ratings',
        target: 'low_email',
        value: belowThresholdCount,
        color: FLOW_COLORS.low,
        conversionRate: 100
      });
      
      if (feedbackFormData.count > 0) {
        links.push({
          source: 'low_email',
          target: 'feedback_form_shown',
          value: feedbackFormData.count,
          color: FLOW_COLORS.low,
          conversionRate: Math.min(100, Math.max(0, belowThresholdCount > 0 ? (feedbackFormData.count / belowThresholdCount) * 100 : 0))
        });
        
        if (reviewSubmittedData.count > 0) {
          links.push({
            source: 'feedback_form_shown',
            target: 'review_submitted',
            value: reviewSubmittedData.count,
            color: FLOW_COLORS.low,
            conversionRate: Math.min(100, Math.max(0, feedbackFormData.count > 0 ? (reviewSubmittedData.count / feedbackFormData.count) * 100 : 0))
          });
        }
      }
    }
    
    // Reward flow links
    for (let i = 0; i < rewardTrackingSteps.length - 1; i++) {
      const current = rewardTrackingSteps[i];
      const next = rewardTrackingSteps[i + 1];
      
      if (current.count > 0 && next.count > 0) {
        links.push({
          source: current.id,
          target: next.id,
          value: next.count,
          color: FLOW_COLORS.rewards,
          conversionRate: Math.min(100, Math.max(0, current.count > 0 ? (next.count / current.count) * 100 : 0))
        });
      }
    }
    
    return links;
  };
  
  const flowLinks = generateLinks(processedSteps);
  
  // Organize by branches
  const branches = {
    main: processedSteps.filter(step => !step.branch),
    high: processedSteps.filter(step => step.branch === 'high'),
    low: processedSteps.filter(step => step.branch === 'low'),
    rewards: processedSteps.filter(step => step.branch === 'rewards')
  };
  
  // Calculate metrics with proper bounds checking
  const metrics = {
    totalSessions: pageLoadedData.count,
    overallConversion: Math.min(100, Math.max(0, pageLoadedData.count > 0 ? 
      ((redirectedData.count + reviewSubmittedData.count) / pageLoadedData.count) * 100 : 0)),
    // Fix: Use consistent data source - calculate high rating rate from actual star rating data
    highRatingRate: Math.min(100, Math.max(0, totalRatings > 0 ? 
      (aboveThresholdCount / totalRatings) * 100 : 0)),
    rewardRedemptionRate: Math.min(100, Math.max(0, rewardTrackingSteps.length > 0 && rewardTrackingSteps[0]?.count > 0 ? 
      (rewardTrackingSteps[rewardTrackingSteps.length - 1]?.count || 0) / rewardTrackingSteps[0].count * 100 : 0))
  };
  
  return {
    steps: processedSteps,
    links: flowLinks,
    branches,
    metrics
  };
}